import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ESTADO_CUOTA, ESTADO_PLAN, CANALES, MOTIVOS, eur, fechaCuota } from './paymentPlanUtils';

/**
 * Un plan de pagos tal como lo devuelve el servidor (con `overdue_days` por
 * cuota, `next_installment`, `paid_total`, `pending_amount` y `debt_mismatch`).
 * Solo pinta: las acciones van fuera.
 */
const canal = v => CANALES.find(c => c.value === v)?.label || v;
const motivo = v => MOTIVOS.find(m => m.value === v)?.label || v;

const PaymentPlanDetail = ({ plan }) => {
  const vencidas = plan.installments.filter(c => c.overdue_days > 0);
  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600'>
        <span>
          <strong className='text-slate-800'>{ESTADO_PLAN[plan.status] || plan.status}</strong> · acordado el{' '}
          {fechaCuota(plan.agreed_at || plan.created_at)}
        </span>
        <span>
          {plan.installments.length} cuotas · {eur(plan.total_amount)}
        </span>
        <span>
          Pagado {eur(plan.paid_total)} · queda {eur(plan.pending_amount)}
        </span>
        <span>Seguimiento por {canal(plan.channel)}</span>
      </div>

      {plan.debt_mismatch && (
        <div className='flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800'>
          <ExclamationTriangleIcon className='w-5 h-5 shrink-0' />
          <p>
            La deuda viva ({eur(plan.current_debt)}) es mayor que lo que queda del plan ({eur(plan.pending_amount)}). Ha
            subido después del alta: el plan no la cubre entera.
          </p>
        </div>
      )}
      {plan.status === 'vigente' && vencidas.length > 0 && (
        <p className='text-sm text-rose-700'>
          {vencidas.length === 1 ? '1 cuota vencida' : `${vencidas.length} cuotas vencidas`} sin pagar del todo.
        </p>
      )}

      <table className='min-w-full text-sm text-slate-700'>
        <thead className='text-xs text-slate-500 bg-slate-50'>
          <tr>
            <th className='px-3 py-2 text-left font-medium'>#</th>
            <th className='px-3 py-2 text-left font-medium'>Vence</th>
            <th className='px-3 py-2 text-right font-medium'>Importe</th>
            <th className='px-3 py-2 text-right font-medium'>Pagado</th>
            <th className='px-3 py-2 text-left font-medium'>Estado</th>
          </tr>
        </thead>
        <tbody>
          {plan.installments.map(c => {
            const estado = ESTADO_CUOTA[c.status] || ESTADO_CUOTA.pendiente;
            return (
              <tr key={c.number} className='border-t border-slate-100'>
                <td className='px-3 py-2 text-slate-500'>{c.number}</td>
                <td className='px-3 py-2'>{fechaCuota(c.due_date)}</td>
                <td className='px-3 py-2 text-right'>{eur(c.amount)}</td>
                <td className='px-3 py-2 text-right'>{eur(c.paid)}</td>
                <td className='px-3 py-2'>
                  <span className={`inline-flex px-2 py-0.5 rounded-lg text-xs font-medium ${estado.className}`}>{estado.label}</span>
                  {c.status === 'pagada' && c.days_late > 0 && (
                    <span className='ml-2 text-xs text-amber-700'>{c.days_late} d tarde</span>
                  )}
                  {c.overdue_days > 0 && <span className='ml-2 text-xs text-rose-700'>vencida hace {c.overdue_days} d</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {plan.notes && <p className='text-sm text-slate-600 whitespace-pre-line'>{plan.notes}</p>}
      {plan.cancellation && (
        <p className='text-sm text-slate-600'>
          Cancelado el {fechaCuota(plan.cancellation.at)} · {motivo(plan.cancellation.reason)}
          {plan.cancellation.note ? ` — ${plan.cancellation.note}` : ''}
        </p>
      )}
    </div>
  );
};

export default PaymentPlanDetail;
